import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../components/Icon';
import KpiCard from '../components/KpiCard';
import Pagination, { PAGE_SIZE, usePagedRows } from '../components/Pagination';
import { useToast } from '../components/Toast';
import { fetchAllPages } from '../api/fetchAll';
import {
  ServiceOrder,
  OrderKind,
  OrderStatus,
  ADMIN_SETTABLE_ORDER_STATUSES,
  listOrders,
  updateOrderStatus,
  statusLabel,
  statusBadgeClass,
  formatAmount,
  extractApiError,
} from '../api/orders';

type KindFilter = 'all' | OrderKind;
type StatusFilter = 'all' | OrderStatus;

const KIND_FILTERS: KindFilter[] = ['all', 'legal', 'maintenance'];
/*
 * Only the statuses an admin can actually set from the Update Status column.
 * 'submitted', 'quoted' and 'declined' come from the customer/provider flows,
 * so they are no longer offered as filters — rows in those states are still
 * listed under 'all' and still show their real badge.
 */
const STATUS_FILTERS: StatusFilter[] = ['all', ...ADMIN_SETTABLE_ORDER_STATUSES];

interface ServiceOrdersProps {
  /**
   * Restricts the page to a single order kind. The Type filter and the per-row
   * kind badge are hidden, since every row would carry the same value. Used by
   * the Service Order page, which is this table locked to 'maintenance'.
   */
  lockedKind?: OrderKind;
}

const ServiceOrders: React.FC<ServiceOrdersProps> = ({ lockedKind }) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [orders, setOrders] = useState<ServiceOrder[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [kind, setKind] = useState<KindFilter>(lockedKind ?? 'all');
  const [status, setStatus] = useState<StatusFilter>('all');
  const [savingId, setSavingId] = useState<number | null>(null);

  /*
   * /admin/orders and /admin/service-requests render this same component at
   * the same position in the tree, so React reuses the instance instead of
   * remounting — the `kind` state initializer does NOT re-run on navigation.
   * lockedKind must therefore win over the state on every render, not just at
   * mount, or Service Order would inherit whatever filter Legal Services
   * Request was left on.
   */
  const effectiveKind: KindFilter = lockedKind ?? kind;

  /*
   * The maintenance-only ("Service Order") view carries no payment
   * information, and repeats the same text in Service and Tier/Category — so
   * those columns are dropped there. The legal-only "Legal Services Request"
   * view also locks its kind, but keeps the payment columns. Each reads with
   * its page's own noun.
   */
  const isMaintenanceView = lockedKind === 'maintenance';
  const noun = isMaintenanceView ? 'Orders' : 'Requests';
  const columns = [
    'Ref',
    'Customer',
    'Service',
    ...(isMaintenanceView ? [] : ['Amount', 'Paid']),
    'Status',
    'Update Status',
    'View',
  ];

  const load = useCallback(async () => {
    setLoading(true);
    try {
      // Every matching order: the KPI cards count them all.
      const all = await fetchAllPages((page, limit) =>
        listOrders({
          kind: effectiveKind === 'all' ? undefined : effectiveKind,
          status: status === 'all' ? undefined : status,
          page,
          limit,
        }).then((res) => ({ rows: res.orders, totalPages: res.totalPages }))
      );
      setOrders(all);
    } catch (err) {
      toast(extractApiError(err, 'Failed to load orders'), 'error');
    } finally {
      setLoading(false);
    }
  }, [effectiveKind, status, toast]);

  useEffect(() => {
    load();
  }, [load]);

  const changeStatus = async (order: ServiceOrder, next: OrderStatus) => {
    if (next === order.status) return;
    setSavingId(order.id);
    try {
      const updated = await updateOrderStatus(order.id, next);
      // Merge the whole server row (rebuilt timeline, new updatedAt), not just `status`.
      setOrders((prev) => prev.map((o) => (o.id === order.id ? { ...o, ...updated } : o)));
      toast(`${order.orderRef || 'Order'} → ${statusLabel(next)}`, 'success');
      // If a status filter is active and the row no longer matches, drop it.
      if (status !== 'all' && updated.status !== status) {
        setOrders((prev) => prev.filter((o) => o.id !== order.id));
      }
    } catch (err) {
      toast(extractApiError(err, 'Failed to update status'), 'error');
    } finally {
      setSavingId(null);
    }
  };

  const kpis = useMemo(() => {
    const submitted = orders.filter((o) => o.status === 'submitted').length;
    const inProgress = orders.filter((o) => o.status === 'in_progress').length;
    const completed = orders.filter((o) => o.status === 'completed').length;
    return { total: orders.length, submitted, inProgress, completed };
  }, [orders]);

  const { page, setPage, pageRows, total } = usePagedRows(orders, PAGE_SIZE, `${effectiveKind}|${status}`);

  return (
    <div>
      <div className="flex gap-4 flex-wrap mb-6">
        <KpiCard icon="package" label={`Total ${noun}`} value={String(kpis.total)} change="This view" up delay={0} />
        <KpiCard icon="message-square" label="Submitted" value={String(kpis.submitted)} change="Awaiting action" up delay={80} />
        <KpiCard icon="wrench" label="In Progress" value={String(kpis.inProgress)} change="Being worked" up delay={160} />
        <KpiCard icon="check-circle" label="Completed" value={String(kpis.completed)} change="All time" up delay={240} />
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 mb-5">
        {!lockedKind && (
          <div className="flex gap-2 flex-wrap items-center">
            <span className="text-xs font-semibold text-g500 uppercase tracking-wider w-14">Type</span>
            {KIND_FILTERS.map((k) => {
              const active = kind === k;
              return (
                <button
                  key={k}
                  onClick={() => setKind(k)}
                  className="capitalize"
                  style={{
                    padding: '7px 15px',
                    borderRadius: 10,
                    fontSize: 13,
                    fontWeight: 600,
                    border: `1px solid ${active ? 'var(--brand)' : 'var(--g200)'}`,
                    background: active ? 'var(--brand-light)' : '#fff',
                    color: active ? 'var(--brand)' : 'var(--g600)',
                  }}
                >
                  {k}
                </button>
              );
            })}
          </div>
        )}
        <div className="flex gap-2 flex-wrap items-center">
          <span className="text-xs font-semibold text-g500 uppercase tracking-wider w-14">Status</span>
          {STATUS_FILTERS.map((s) => {
            const active = status === s;
            return (
              <button
                key={s}
                onClick={() => setStatus(s)}
                className="capitalize"
                style={{
                  padding: '7px 15px',
                  borderRadius: 10,
                  fontSize: 13,
                  fontWeight: 600,
                  border: `1px solid ${active ? 'var(--brand)' : 'var(--g200)'}`,
                  background: active ? 'var(--brand-light)' : '#fff',
                  color: active ? 'var(--brand)' : 'var(--g600)',
                }}
              >
                {s === 'all' ? 'all' : statusLabel(s as OrderStatus)}
              </button>
            );
          })}
        </div>
      </div>

      <div
        className="bg-white rounded-card overflow-x-auto border border-g100"
        style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)' }}
      >
        <table className="w-full border-collapse">
          <thead>
            <tr>
              {columns.map((h) => (
                <th
                  key={h}
                  className="px-4 py-3 text-left text-xs font-semibold text-g500 uppercase tracking-wider border-b border-g200 bg-g50 whitespace-nowrap"
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-g500">
                  Loading {noun.toLowerCase()}…
                </td>
              </tr>
            ) : orders.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-10 text-center text-sm text-g500">
                  No {noun.toLowerCase()} match this filter.
                </td>
              </tr>
            ) : (
              pageRows.map((o) => (
                <tr key={o.id}>
                  <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                    {o.orderRef || `#${o.id}`}
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <div className="font-semibold">{o.customer?.name || `User #${o.userId}`}</div>
                    {o.customer?.email && (
                      <div className="text-[12px] text-g500">{o.customer.email}</div>
                    )}
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <span className="flex items-center gap-2">
                      {!lockedKind && (
                        <span
                          className={
                            'type-badge ' + (o.kind === 'legal' ? 'type-rental' : 'type-sale')
                          }
                        >
                          {o.kind}
                        </span>
                      )}
                      {o.serviceName || o.category || '—'}
                    </span>
                  </td>
                  {!isMaintenanceView && (
                    <>
                      <td className="px-4 py-3.5 text-sm font-semibold whitespace-nowrap border-b border-g100">
                        {formatAmount(o.amount)}
                      </td>
                      <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                        {o.paid ? (
                          <span className="inline-flex items-center gap-1" style={{ color: 'var(--green)' }}>
                            <Icon name="check-circle" size={14} color="var(--green)" /> Paid
                          </span>
                        ) : (
                          <span className="text-g500">Unpaid</span>
                        )}
                      </td>
                    </>
                  )}
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <span className={statusBadgeClass(o.status)}>{statusLabel(o.status)}</span>
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <select
                      value={o.status}
                      disabled={savingId === o.id}
                      onChange={(e) => changeStatus(o, e.target.value as OrderStatus)}
                      style={{
                        padding: '7px 10px',
                        borderRadius: 9,
                        fontSize: 13,
                        fontWeight: 600,
                        border: '1px solid var(--g200)',
                        background: savingId === o.id ? 'var(--g100)' : '#fff',
                        color: 'var(--g700)',
                        cursor: savingId === o.id ? 'wait' : 'pointer',
                      }}
                    >
                      {/* A <select> whose value matches no <option> renders the
                          first one instead, which would misreport the row's
                          real status. Keep the current status listed (disabled)
                          when admin isn't allowed to set it. */}
                      {!ADMIN_SETTABLE_ORDER_STATUSES.includes(o.status) && (
                        <option value={o.status} disabled>
                          {statusLabel(o.status)}
                        </option>
                      )}
                      {ADMIN_SETTABLE_ORDER_STATUSES.map((s) => (
                        <option key={s} value={s}>
                          {statusLabel(s)}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-4 py-3.5 text-sm whitespace-nowrap border-b border-g100">
                    <button
                      onClick={() =>
                        navigate(isMaintenanceView ? `/admin/service-requests/${o.id}` : `/admin/orders/${o.id}`)
                      }
                      title="View full details"
                      className="inline-flex items-center gap-1.5"
                      style={{
                        padding: '7px 14px',
                        borderRadius: 9,
                        fontSize: 13,
                        fontWeight: 600,
                        border: '1px solid var(--g200)',
                        background: '#fff',
                        color: 'var(--g700)',
                        cursor: 'pointer',
                      }}
                    >
                      <Icon name="eye" size={14} color="var(--brand)" />
                      View
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={total} onChange={setPage} disabled={loading} />
    </div>
  );
};

export default ServiceOrders;
